// Task Database with API Integration
// Hybrid approach: Uses API when available, falls back to localStorage
// Automatically syncs from API to localStorage for offline capability

const API_URL = process.env.REACT_APP_API_URL || '';
const USE_API = !!API_URL;

class TaskDatabaseAPI {
  constructor() {
    this.storageKey = 'admin_tasks';
    this.lastSyncKey = 'admin_tasks_last_sync';
    this.initializeStorage();

    // Auto-sync from API on initialization if available
    if (USE_API) {
      this.syncFromAPI().catch(err => {
        console.warn('Initial API sync failed, using localStorage:', err);
      });
    }
  }

  initializeStorage() {
    if (!localStorage.getItem(this.storageKey)) {
      localStorage.setItem(this.storageKey, JSON.stringify({
        tasks: [],
        tags: ['creative', 'listing', 'advertising', 'client-work', 'internal', 'urgent'],
        nextTaskId: 1
      }));
    }
  }

  getData() {
    return JSON.parse(localStorage.getItem(this.storageKey));
  }

  saveData(data) {
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }

  // API helper method
  async apiRequest(endpoint, options = {}) {
    if (!USE_API) {
      throw new Error('API not configured');
    }

    const url = `${API_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || `API request failed: ${response.status}`);
    }

    return response.json();
  }

  // Sync tasks from API to localStorage
  async syncFromAPI() {
    try {
      const response = await this.apiRequest('/tasks');
      const apiTasks = response.tasks || [];

      const localData = this.getData();
      localData.tasks = apiTasks;
      this.saveData(localData);

      localStorage.setItem(this.lastSyncKey, new Date().toISOString());

      console.log(`Synced ${apiTasks.length} tasks from API`);
      return apiTasks;
    } catch (error) {
      console.error('Failed to sync from API:', error);
      throw error;
    }
  }

  // Get all tasks (from API if available, localStorage as fallback)
  async getAllTasks() {
    if (USE_API) {
      try {
        const response = await this.apiRequest('/tasks');
        const tasks = response.tasks || [];

        // Update localStorage cache
        const localData = this.getData();
        localData.tasks = tasks;
        this.saveData(localData);

        return tasks;
      } catch (error) {
        console.warn('API request failed, using localStorage:', error);
        return this.getData().tasks;
      }
    } else {
      return this.getData().tasks;
    }
  }

  // Create a new task
  async createTask({ title, description, dueDate, tags, priority, clientId, emailReference }) {
    const taskData = {
      title: title || '',
      description: description || '',
      dueDate: dueDate || null,
      tags: tags || [],
      priority: priority || 'medium',
      clientId: clientId || null,
      emailReference: emailReference || null
    };

    if (USE_API) {
      try {
        const response = await this.apiRequest('/tasks', {
          method: 'POST',
          body: JSON.stringify(taskData)
        });

        const newTask = response.task;

        // Update localStorage cache
        const localData = this.getData();
        localData.tasks.push(newTask);
        this.saveData(localData);

        return newTask;
      } catch (error) {
        console.warn('API request failed, creating task locally:', error);
        return this.createTaskLocally(taskData);
      }
    } else {
      return this.createTaskLocally(taskData);
    }
  }

  // Create task locally (fallback)
  createTaskLocally(taskData) {
    const data = this.getData();

    const newTask = {
      id: `task_${data.nextTaskId}_${Date.now()}`,
      ...taskData,
      status: 'todo',
      createdAt: new Date().toISOString(),
      completedAt: null,
      updatedAt: new Date().toISOString()
    };

    data.tasks.push(newTask);
    data.nextTaskId++;
    this.saveData(data);

    return newTask;
  }

  // Update task
  async updateTask(taskId, updates) {
    if (USE_API) {
      try {
        const response = await this.apiRequest(`/tasks/${taskId}`, {
          method: 'PUT',
          body: JSON.stringify(updates)
        });

        const updatedTask = response.task;

        // Update localStorage cache
        const localData = this.getData();
        const taskIndex = localData.tasks.findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
          localData.tasks[taskIndex] = updatedTask;
          this.saveData(localData);
        }

        return { success: true, task: updatedTask };
      } catch (error) {
        console.warn('API request failed, updating task locally:', error);
        return this.updateTaskLocally(taskId, updates);
      }
    } else {
      return this.updateTaskLocally(taskId, updates);
    }
  }

  // Update task locally (fallback)
  updateTaskLocally(taskId, updates) {
    const data = this.getData();
    const taskIndex = data.tasks.findIndex(task => task.id === taskId);

    if (taskIndex === -1) {
      return { success: false, error: 'Task not found' };
    }

    data.tasks[taskIndex] = {
      ...data.tasks[taskIndex],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    this.saveData(data);
    return { success: true, task: data.tasks[taskIndex] };
  }

  // Delete task
  async deleteTask(taskId) {
    if (USE_API) {
      try {
        await this.apiRequest(`/tasks/${taskId}`, {
          method: 'DELETE'
        });

        // Update localStorage cache
        const localData = this.getData();
        localData.tasks = localData.tasks.filter(t => t.id !== taskId);
        this.saveData(localData);

        return { success: true };
      } catch (error) {
        console.warn('API request failed, deleting task locally:', error);
        return this.deleteTaskLocally(taskId);
      }
    } else {
      return this.deleteTaskLocally(taskId);
    }
  }

  // Delete task locally (fallback)
  deleteTaskLocally(taskId) {
    const data = this.getData();
    const taskIndex = data.tasks.findIndex(task => task.id === taskId);

    if (taskIndex === -1) {
      return { success: false, error: 'Task not found' };
    }

    data.tasks.splice(taskIndex, 1);
    this.saveData(data);
    return { success: true };
  }

  // Mark task as completed
  async completeTask(taskId) {
    return this.updateTask(taskId, {
      status: 'completed',
      completedAt: new Date().toISOString()
    });
  }

  // Mark task as in progress
  async startTask(taskId) {
    return this.updateTask(taskId, {
      status: 'in-progress'
    });
  }

  // Reopen completed task
  async reopenTask(taskId) {
    return this.updateTask(taskId, {
      status: 'todo',
      completedAt: null
    });
  }

  // Get task by ID
  getTask(taskId) {
    const data = this.getData();
    return data.tasks.find(task => task.id === taskId);
  }

  // Get tasks by status
  getTasksByStatus(status) {
    const data = this.getData();
    return data.tasks.filter(task => task.status === status);
  }

  // Get tasks by tag
  getTasksByTag(tag) {
    const data = this.getData();
    return data.tasks.filter(task => task.tags.includes(tag));
  }

  // Get tasks by client
  getTasksByClient(clientId) {
    const data = this.getData();
    return data.tasks.filter(task => task.clientId === clientId);
  }

  // Get overdue tasks
  getOverdueTasks() {
    const data = this.getData();
    const now = new Date();

    return data.tasks.filter(task => {
      if (!task.dueDate || task.status === 'completed') return false;
      return new Date(task.dueDate) < now;
    });
  }

  // Get tasks due soon (within next 7 days)
  getTasksDueSoon(days = 7) {
    const data = this.getData();
    const now = new Date();
    const future = new Date();
    future.setDate(future.getDate() + days);

    return data.tasks.filter(task => {
      if (!task.dueDate || task.status === 'completed') return false;
      const dueDate = new Date(task.dueDate);
      return dueDate >= now && dueDate <= future;
    });
  }

  // Get all available tags
  getAllTags() {
    const data = this.getData();
    return data.tags;
  }

  // Add a custom tag
  addTag(tagName) {
    const data = this.getData();
    const normalizedTag = tagName.toLowerCase().trim();

    if (!data.tags.includes(normalizedTag)) {
      data.tags.push(normalizedTag);
      this.saveData(data);
      return { success: true, tag: normalizedTag };
    }

    return { success: false, error: 'Tag already exists' };
  }

  // Get task statistics
  getStatistics() {
    const data = this.getData();
    const stats = {
      total: data.tasks.length,
      todo: data.tasks.filter(t => t.status === 'todo').length,
      inProgress: data.tasks.filter(t => t.status === 'in-progress').length,
      completed: data.tasks.filter(t => t.status === 'completed').length,
      overdue: this.getOverdueTasks().length,
      dueSoon: this.getTasksDueSoon().length
    };

    return stats;
  }

  // Search tasks
  searchTasks(query) {
    const data = this.getData();
    const lowerQuery = query.toLowerCase();

    return data.tasks.filter(task =>
      task.title.toLowerCase().includes(lowerQuery) ||
      task.description.toLowerCase().includes(lowerQuery) ||
      task.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  }

  // Create task from email (used by webhook)
  async createTaskFromEmail({ subject, from, body, receivedAt }) {
    return this.createTask({
      title: subject || 'Task from email',
      description: body || '',
      emailReference: {
        from,
        subject,
        receivedAt: receivedAt || new Date().toISOString(),
        body
      },
      tags: [],
      priority: 'medium'
    });
  }

  // Get last sync time
  getLastSyncTime() {
    return localStorage.getItem(this.lastSyncKey);
  }

  // Check if API is configured
  isAPIEnabled() {
    return USE_API;
  }
}

// Create and export singleton instance
const taskDB = new TaskDatabaseAPI();
export default taskDB;
